import { RecipeStep } from "./recipeLab";

export type RuntimeEstimatorConfig = {
  FULL_SPEED: number;            // from machine config
  ONE_LAP_FULL_SPEED_MS: number; // from machine config
  GENERAL_SPEED: number;         // used by spray-swipe
  compressorStartDelay: number;  // used by spray-setup
};

type RecipeEstimate = {
    id: string,
    ms: number
}

export type RuntimeEstimate = {
  sprayMs: number;
  fanMs: number;
  totalMs: number;
  recipeEstimates: RecipeEstimate[]
};

export function estimateRecipeRuntime(
  instructions: RecipeStep[],
  cfg: RuntimeEstimatorConfig
): RuntimeEstimate {
  const getCfgNumber = (
    instruction: RecipeStep,
    key: string,
    fallback = 0
  ): number => {
    const item = instruction.configuration?.find((c) => c.key === key);
    const n = Number(item?.value);
    return Number.isFinite(n) ? n : fallback;
  };

  const getRotationTimeOnPlate = (rotationSpeed: number, revs = 1): number => {
    if (rotationSpeed === 0) return 0; 
    return Math.floor(
      cfg.ONE_LAP_FULL_SPEED_MS *
        ((cfg.FULL_SPEED + 1) / (rotationSpeed + 1)) *
        revs
    );
  };

  let sprayMs = 0;
  let fanMs = 0;
  let otherMs = 0;

  const recipeEstimates = [];

  for (let i = 0; i < instructions.length; i++) {
    const current = instructions[i];
    const next = instructions[i + 1];

    let t = 0;

    switch (current.type) {
      case 'spray-setup':
        t = cfg.compressorStartDelay;
        sprayMs += t;
        recipeEstimates.push({ id: current?.id, ms: t })
        break;

      case 'spray-swipe': {
        const swipes = getCfgNumber(current, 'Swipes', 0);
        const servoMsPerDeg = getCfgNumber(current, 'TimeForServoToTurnOneDegree', 0);
        const anglePos = Math.abs(getCfgNumber(current, 'SwipeAnglePos', 0));
        const angleNeg = Math.abs(getCfgNumber(current, 'SwipeAngleNeg', 0));
        const oneDegreeMilliseconds = servoMsPerDeg * 1000 / (anglePos + angleNeg)
        t =
          getRotationTimeOnPlate(cfg.GENERAL_SPEED, 1) / 2 +
          500 +
          swipes * (oneDegreeMilliseconds * (angleNeg + anglePos));

        sprayMs += t;
        recipeEstimates.push({ id: current?.id, ms: t })
        break;
      }

      case 'spray-rotate': {
        const speed = getCfgNumber(current, 'Speed', cfg.GENERAL_SPEED);
        const laps = getCfgNumber(current, 'Laps', 1);
        const clockwise = Boolean(
          current.configuration?.find((c) => c.key === 'Clockwise')?.value
        );

        t = getRotationTimeOnPlate(speed, laps);

        if (next?.type === 'spray-rotate') {
          const nextClockwise = Boolean(
            next.configuration?.find((c) => c.key === 'Clockwise')?.value
          );
          if (nextClockwise !== clockwise) t += 1000;
        }

        sprayMs += t;
        recipeEstimates.push({ id: current?.id, ms: t })
        break;
      }

      case 'spray-finilize':
        t = 0;
        sprayMs += t;
        recipeEstimates.push({ id: current?.id, ms: t })
        break;

      case 'fan-start':
        t = 2000;
        fanMs += t;
        recipeEstimates.push({ id: current?.id, ms: t })
        break;

      case 'fan-stop':
        t = 500;
        fanMs += t;
        recipeEstimates.push({ id: current?.id, ms: t })
        break;

      case 'rotate':
        t = getCfgNumber(current, 'Time', 0);
        fanMs += t;
        recipeEstimates.push({ id: current?.id, ms: t })
        break;

      case 'sleep':
        t = getCfgNumber(current, 'Time', 0);
        fanMs += t;
        recipeEstimates.push({ id: current?.id, ms: t })
        break;

      case 'heaters-start':
        t = 1000;
        otherMs += t;
        recipeEstimates.push({ id: current?.id, ms: t })
        break;
    }
  }

  return {
    sprayMs: Math.round(sprayMs),
    fanMs: Math.round(fanMs),
    totalMs: Math.round(sprayMs + fanMs + otherMs),
    recipeEstimates: recipeEstimates
  };
}